import HeartIcon from "@/assets/heart.svg";
import LogoutIcon from "@/assets/logout.svg";
import NewMessagesIcon from "@/assets/msg-new.svg";
import MessagesNoneIcon from "@/assets/msg-none.svg";
import SearchIcon from "@/assets/search.svg";
import SettingsIcon from "@/assets/settings.svg";
import TeamzIcon from "@/assets/teamz.svg";
import TournamentsIcon from "@/assets/tournaments.svg";
import { useClerk } from "@clerk/react";
import { useMutation } from "@tanstack/react-query";
import { Ellipsis, ShieldUser, Zap } from "lucide-react";
import { type PropsWithChildren, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { Link, NavLink } from "react-router";

import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useMe } from "@/context/user-context";
import { useHasUnreadMessages } from "@/hooks/use-has-unread-msg";
import { useToast } from "@/hooks/use-toast";
import { useTRPC } from "@/lib/trpc";
import { cn, toggleLanguage } from "@/lib/utils";
import { routes } from "@/routes";

import { Sheet, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "../ui/sheet";
import { navLinkVariants } from "./navlink.styles";

export const LogoutConfirmationModal = ({ modalOpen, closeModal, signOut }: { modalOpen: boolean; closeModal: () => void; signOut: () => void }) => {
  const { t } = useTranslation(["common"]);
  return (
    <Sheet open={modalOpen} onOpenChange={closeModal}>
      <SheetContent side="center" className="h-64 md:h-80">
        <SheetHeader>
          <SheetTitle>{t("common:logout")}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-1 items-center justify-center">
          <span className="text-balance px-2 text-center text-lg font-medium">
            {t("common:logout-confirm")}
          </span>
        </div>

        <SheetFooter>
          <SheetClose asChild>
            <Button variant="dark" type="button">
              {t("common:cancel")}
            </Button>
          </SheetClose>
          <Button type="button" onClick={signOut}>
            {t("common:logout-confirm-btn")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export function NavMobile({ children }: PropsWithChildren) {
  const { user } = useMe();
  const { openUserProfile, signOut } = useClerk();
  const { t, i18n } = useTranslation(["common"]);
  const [modalState, updateModalState] = useState<"open" | "close">("close");
  const { hasUnread } = useHasUnreadMessages();
  const { toast } = useToast();
  const trpc = useTRPC();

  const { mutate } = useMutation(trpc.payment.createBillingPortalSession.mutationOptions({
    onSuccess: (data) => {
      if (data) {
        globalThis.window.location.replace(data);
      }
    },
    onError: () => {
      toast({
        title: t("common:error-occured"),
        description: "Une erreur est survenue, veuillez réessayer",
      });
    },
  }));

  const handleMutate = () => {
    if (!user.stripeId)
      return;
    mutate({
      urlPath: window.location.pathname,
    });
  };

  const playerNavlinks = (
    <>
      <NavLink
        to={routes.clubs}
        className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}
      >
        <Button variant="mobile-nav-link">
          <SearchIcon />
          {t("mobile-nav.clubs")}
        </Button>
      </NavLink>

      <NavLink
        to={routes.tournaments}
        className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}
      >
        <Button variant="mobile-nav-link">
          <TournamentsIcon />
          {t("mobile-nav.tournaments")}
        </Button>
      </NavLink>

      <NavLink
        to={routes.favorites}
        className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}
      >
        <Button variant="mobile-nav-link">
          <HeartIcon />
          {t("mobile-nav.favorites")}
        </Button>
      </NavLink>

      <NavLink
        to={routes.messages}
        className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}
      >
        <Button variant="mobile-nav-link">
          {hasUnread
            ? <NewMessagesIcon />
            : <MessagesNoneIcon />}
          {t("mobile-nav.messages")}
        </Button>
      </NavLink>
    </>
  );

  const clubNavlinks = (
    <>
      {/* Joueurs reste ouvert aux clubs freemium : ils y voient la liste
          anonymisée, c'est le point d'entrée de la conversion. */}
      <NavLink
        to={routes.players}
        className={({ isActive }) => cn(navLinkVariants({ state: isActive ? "active" : null }), "relative")}
      >
        <Button variant="mobile-nav-link">
          <SearchIcon />
          {t("mobile-nav.players")}
        </Button>
      </NavLink>

      <NavLink
        to={routes.tournaments}
        className={({ isActive }) => cn(navLinkVariants({ state: isActive ? "active" : null }), "relative")}
      >
        <Button variant="mobile-nav-link">
          <TournamentsIcon />
          {t("mobile-nav.tournaments")}
        </Button>
      </NavLink>

      <NavLink
        to={routes.favorites}
        className={({ isActive }) => cn(navLinkVariants({ state: isActive ? "active" : null }), "relative")}
      >
        <Button variant="mobile-nav-link">
          <HeartIcon />
          {t("mobile-nav.favorites")}
        </Button>
      </NavLink>

      <NavLink
        to={routes.messages}
        className={({ isActive }) => navLinkVariants({ state: isActive ? "active" : null })}
      >
        <Button variant="mobile-nav-link">
          {hasUnread
            ? <NewMessagesIcon />
            : <MessagesNoneIcon />}
          {t("mobile-nav.messages")}
        </Button>
      </NavLink>
    </>
  );

  return (
    <>
      <div className="flex min-h-svh flex-col">
        <header className="sticky inset-x-0 top-0 z-50 flex h-[72px] w-full shrink-0 items-center justify-between bg-white px-4 shadow-sm">
          <div className="flex items-center gap-3">
            <Link to={routes.profile}>
              <img src={user.imgUrl} alt={`Photo de ${user.name}`} className="size-[34px] rounded-full object-cover" />
            </Link>

            {user.isAdmin && (
              <Link to={routes.admin} className="flex size-[34px] items-center justify-center rounded-full border bg-neutral-100">
                <ShieldUser strokeWidth={1.5} size={28} />
              </Link>
            )}
          </div>

          <Link to={user.role === "player" ? routes.clubs : routes.players} className="absolute left-1/2 -translate-x-1/2">
            <TeamzIcon className="h-8 w-14" />
          </Link>

          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Ellipsis className="size-6" />
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuLabel>
                  {t("common:menu")}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => toggleLanguage(i18n)}>
                  {i18n.language === "en-US"
                    ? (
                        <>
                          <CircleFlag countryCode="fr" className="size-5 rounded-full" />
                          <span>Switch to French</span>
                        </>
                      )
                    : (
                        <>
                          <CircleFlag countryCode="gb" className="size-5 rounded-full" />
                          <span>Switch to English</span>
                        </>
                      )}
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => openUserProfile()}>
                  <SettingsIcon className="size-4" />
                  {t("common:settings")}
                </DropdownMenuItem>
                {user.role === "club" && (
                  <DropdownMenuItem onClick={handleMutate} disabled={!user.stripeId}>
                    <Zap className="size-4" strokeWidth={1.5} />
                    {t("common:plan.plans-nav")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => updateModalState("open")}>
                  <LogoutIcon className="size-4" />
                  {t("common:logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 bg-neutral-100 pb-16">
          {children}
        </main>

        <footer className="fixed inset-x-0 bottom-0 z-50 h-16 w-full shrink-0 border-t border-neutral-200 bg-white text-white shadow-sm">
          <div className="mx-auto flex h-full max-w-[960px] items-center justify-between gap-1 px-2">
            {user.role === "player" && playerNavlinks}
            {user.role === "club" && clubNavlinks}
          </div>
        </footer>
      </div>

      <LogoutConfirmationModal
        modalOpen={modalState === "open"}
        closeModal={() => updateModalState("close")}
        signOut={() => signOut({
          redirectUrl: routes.signup,
        })}
      />
    </>
  );
}
